/**
 * Project download popup: the anchored trigger plus the popup body with the two
 * credit counters and the three entitlement-gated export buttons.
 *
 * Extracted from `ProfilerProjectView` (TD-2970) so the Team feedback view can
 * mount the same controls for feedback recipients. All state comes from
 * `useProjectDownloads`; this file only renders it.
 *
 * `ProjectDownloadPopupBody` is exported on its own because the mobile sheet
 * (see `ProjectDownloadOverlays`) hosts the body without the trigger.
 */
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useStore } from '../../../store/store';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../buttons/IconButton';
import SubscriptionDownloadCounter from '../../common/SubscriptionDownloadCounter';
import EnrichmentCreditCounter from '../../common/EnrichmentCreditCounter';
import { exportEntryGate } from '../../common/exportAccessTooltips';
import {
  DOWNLOAD_LIMIT_PPTX_ONEPAGER,
  DOWNLOAD_LIMIT_XLSX_CONTACTS,
  DOWNLOAD_LIMIT_XLSX_PROFILES,
  exceedsListCap,
  listCapTooltip
} from '../../../utils/constants/downloadLimits';
import { isProfilerSearchDownloadDisabled } from '../../../utils/constants/variables';
import { PROJECT_DOWNLOAD_TRIGGER_CLASS } from '../../../hooks/useProjectDownloads';
import { projectDownloadsShape } from './projectDownloadsShape';

// The three download buttons each pick their icon from the same pair of flags:
// one helper, short-circuit returns (downloading → done → no icon). Exported for
// the mobile bar's in-progress Download button.
export const downloadStateIcon = ({ isDownloading, isSuccess }) => {
  if (isDownloading) return ICON_BUTTON_ICONS.FILE_EARMARK_ARROW_DOWN;
  if (isSuccess) return ICON_BUTTON_ICONS.FILE_EARMARK_ARROW_DOWN_FILL;
  return null;
};

// Tooltip precedence: no-access → loading → allowance used up → too many
// results → downloading → success → null. A used-up allowance shows no tooltip:
// the counter at the top of the popup already says "Download Limit Reached", and
// the list-cap copy would promise that excluding companies helps when it cannot.
const renderDownloadTooltip = ({
  hasAccess,
  limit,
  isLoading,
  isAllowanceReached,
  isOverCap,
  canExclude,
  isDownloading,
  isSuccess,
  noAccessNode
}) => {
  if (!hasAccess) return noAccessNode;
  if (isLoading) return <>Loading results, please wait.</>;
  if (isAllowanceReached) return null;
  if (isOverCap) return listCapTooltip(limit, { canExclude });
  if (isDownloading) return <>Downloading. Please wait.</>;
  if (isSuccess) return <>Downloaded successfully</>;
  return null;
};

/**
 * One gated export button. `roleAccess` is the tier getter for this artefact;
 * the `profilerexportdisabled` right and the shared disable reasons (allowance
 * reached, loading, row cap, in flight) are applied on top.
 */
const ExportButton = observer(
  ({
    downloads,
    label,
    type,
    roleAccess,
    limit,
    onClick,
    onSelect = null,
    tooltipPosition = null
  }) => {
    const { profilerAccessStore } = useStore();
    const {
      state,
      nonExcludedResultsCount,
      isLoadingResults,
      isSubscriptionLoading,
      isDownloadLimitReached,
      canExcludeCompanies = true
    } = downloads;
    const isDownloading = state[`isDownloadingProfilerSearch${type}List`];
    const isSuccess = state[`isSuccessDownloadProfilerSearch${type}List`];
    const isOverCap = exceedsListCap(nonExcludedResultsCount, limit);

    // TD-2648: the trigger no longer carries the `profilerexportdisabled` right,
    // so each entry inside the popup does.
    const gate = exportEntryGate({
      isExportDisabled: profilerAccessStore.isProfilerExportDisabled,
      hasRoleAccess: roleAccess,
      // Subscription loading too: requestBulkExport is a no-op until the status
      // is in, and an enabled entry would close the mobile sheet for nothing.
      extraDisabled:
        isDownloadLimitReached ||
        isLoadingResults ||
        isSubscriptionLoading ||
        isOverCap ||
        isDownloading
    });

    return (
      <IconButton
        type={ICON_BUTTON_TYPES.BLUE}
        innerText={label}
        withBorder={false}
        onClick={() => {
          onSelect?.();
          onClick();
        }}
        disabled={gate.disabled}
        {...(tooltipPosition ? { tooltipPosition } : {})}
        tooltipClickable={gate.tooltipClickable}
        tooltipContent={renderDownloadTooltip({
          hasAccess: gate.hasAccess,
          limit,
          isLoading: isLoadingResults || isSubscriptionLoading,
          isAllowanceReached: isDownloadLimitReached,
          isOverCap,
          canExclude: canExcludeCompanies,
          isDownloading,
          isSuccess,
          noAccessNode: gate.noAccessNode
        })}
        icon={downloadStateIcon({ isDownloading, isSuccess })}
        iconOnRight
      />
    );
  }
);

ExportButton.propTypes = {
  downloads: projectDownloadsShape.isRequired,
  label: PropTypes.string.isRequired,
  /** Flag-key infix: 'Onepager' | 'Profiles' | 'Contact'. */
  type: PropTypes.oneOf(['Onepager', 'Profiles', 'Contact']).isRequired,
  roleAccess: PropTypes.bool.isRequired,
  limit: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  tooltipPosition: PropTypes.string
};

/**
 * The popup body: download + enrichment counters and the three export buttons.
 * Hosted anchored below the trigger on desktop and inside a MobileSheet on mobile.
 *
 * `onSelect` runs before an entry's action. The MobileSheet passes its close:
 * the sheet is a native `<dialog>` in the top layer, so anything opened after it
 * that is not itself a native dialog (the enrichment contacts dialog, the header
 * error message of a direct download) would sit under its backdrop (TD-3040).
 */
export const ProjectDownloadPopupBody = observer(
  ({ downloads, onSelect = null, onepagerTooltipPosition = null }) => {
    const { profilerAccessStore } = useStore();
    const { requestBulkExport, contactsDownload } = downloads;

    return (
      <div className="download-profiler-search-popup">
        <SubscriptionDownloadCounter label="Downloads" />
        <EnrichmentCreditCounter variant="row" />
        <div className="buttons-wrap">
          <ExportButton
            downloads={downloads}
            label="Download onepager list as PPTX"
            type="Onepager"
            roleAccess={profilerAccessStore.canExportProfilerOnepager}
            limit={DOWNLOAD_LIMIT_PPTX_ONEPAGER}
            onClick={() => requestBulkExport('onepager')}
            onSelect={onSelect}
            tooltipPosition={onepagerTooltipPosition}
          />
          <ExportButton
            downloads={downloads}
            label="Download company list as XLSX"
            type="Profiles"
            roleAccess={profilerAccessStore.canExportProfilerOnepager}
            limit={DOWNLOAD_LIMIT_XLSX_PROFILES}
            onClick={() => requestBulkExport('profiles')}
            onSelect={onSelect}
          />
          <ExportButton
            downloads={downloads}
            label="Download contact list as XLSX"
            type="Contact"
            roleAccess={profilerAccessStore.canDownloadProfilerContactsList}
            limit={DOWNLOAD_LIMIT_XLSX_CONTACTS}
            onClick={contactsDownload.requestDownload}
            onSelect={onSelect}
          />
        </div>
      </div>
    );
  }
);

ProjectDownloadPopupBody.propTypes = {
  downloads: projectDownloadsShape.isRequired,
  /** Called before an entry's action; the mobile sheet passes its close. */
  onSelect: PropTypes.func,
  onepagerTooltipPosition: PropTypes.string
};

/** Trigger tooltip while the popup is closed (S3358: no nested ternary in JSX). */
const triggerTooltip = () =>
  isProfilerSearchDownloadDisabled ? <>Upcoming feature</> : <>Download search</>;

/**
 * Trigger icon plus, while open, the popup body anchored below it. The host
 * decides the placement and gives the trigger its DOM id via `idProps`.
 */
const ProjectDownloadControls = observer(
  ({ downloads, idProps = {}, onepagerTooltipPosition = null }) => {
    const { state } = downloads;

    return (
      <>
        <IconButton
          {...idProps}
          type={ICON_BUTTON_TYPES.BLUE}
          icon={ICON_BUTTON_ICONS.DOWNLOAD}
          wrapClassName={PROJECT_DOWNLOAD_TRIGGER_CLASS}
          onClick={state.toggle}
          filled={state.isOpen}
          tooltipContent={state.isOpen ? null : triggerTooltip()}
          disabled={isProfilerSearchDownloadDisabled}
        />
        {state.isOpen && (
          <ProjectDownloadPopupBody
            downloads={downloads}
            onepagerTooltipPosition={onepagerTooltipPosition}
          />
        )}
      </>
    );
  }
);

ProjectDownloadControls.propTypes = {
  downloads: projectDownloadsShape.isRequired,
  /** Spread onto the trigger IconButton, e.g. `{ id: '…-download-results-button-desktop' }`. */
  idProps: PropTypes.object,
  onepagerTooltipPosition: PropTypes.string
};

export default ProjectDownloadControls;
